import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api, fileUrl, FALLBACK_COVER } from "@/lib/api";
import { usePlayer } from "@/context/PlayerContext";
import { PlayButton } from "@/components/site/ReleaseCard";
import { PlatformLinks } from "@/components/site/PlatformLinks";
import { Eq } from "@/components/site/CdjPlayerBar";
import { ShareButton } from "@/components/site/ShareButton";
import { SITE } from "@/components/site/Seo";
import { Waveform, useTrackWave } from "@/components/site/Waveform";
import { SocialShare } from "@/components/site/SocialShare";
import { BuyTrackButton } from "@/components/site/BuyTrack";

const setMeta = (sel, val) => document.querySelector(sel)?.setAttribute("content", val);

const releaseJsonLd = (rel) => ({
  "@context": "https://schema.org", "@type": "MusicAlbum", name: rel.title, url: `${SITE}/uscite/${rel.id}`,
  ...(rel.cover_url?.startsWith("/api/") ? { image: `${SITE}${rel.cover_url}` } : {}),
  byArtist: { "@type": "MusicGroup", name: rel.artist_name || "The Groove Anomaly records" },
  recordLabel: { "@type": "Organization", name: "The Groove Anomaly records", url: `${SITE}/` },
  ...(rel.genre ? { genre: rel.genre } : {}), ...(rel.release_date ? { datePublished: rel.release_date } : {}),
  albumReleaseType: { Singolo: "SingleRelease", EP: "EPRelease", Album: "AlbumRelease" }[rel.release_type] ? `https://schema.org/${{ Singolo: "SingleRelease", EP: "EPRelease", Album: "AlbumRelease" }[rel.release_type]}` : undefined,
  numTracks: (rel.tracks || []).length,
  track: (rel.tracks || []).map((t, i) => ({
    "@type": "MusicRecording", position: i + 1, name: t.title, byArtist: { "@type": "MusicGroup", name: rel.artist_name || "The Groove Anomaly records" },
    ...(t.isrc ? { isrcCode: t.isrc } : {}), ...((t.genre || rel.genre) ? { genre: t.genre || rel.genre } : {}),
    ...(t.price >= 1 ? { offers: { "@type": "Offer", price: t.price.toFixed(2), priceCurrency: "EUR", availability: "https://schema.org/InStock", url: `${SITE}/uscite/${rel.id}` } } : {}),
  })),
});

const TrackRow = ({ rel, t, i }) => {
  const { progress, onSeek, isCur, active } = useTrackWave(rel, i);
  const { playing } = usePlayer();
  return (
    <li className={`py-4 px-3 -mx-3 rounded-xl ${isCur ? "bg-emerald-500/10 ring-1 ring-emerald-400/30" : "hover:bg-white/5"} transition-colors`} data-testid={`track-row-${i}`}>
      <div className="flex items-center gap-4">
        <span className="font-mono-tga text-xs text-zinc-500 w-6">{String(i + 1).padStart(2, "0")}</span>
        {t.audio_url ? <PlayButton release={rel} trackIdx={i} testid={`track-play-btn-${i}`} className="h-9 w-9" /> : <span className="h-9 w-9" />}
        <span className={`flex-1 font-semibold ${isCur ? "text-emerald-300" : ""}`}>{t.title}</span>
        {isCur && <Eq active={playing} />}
        <span className="font-mono-tga text-xs text-zinc-400">{t.duration}</span>
        <BuyTrackButton rel={rel} t={t} i={i} />
      </div>
      {t.audio_url && <div className="pl-[4.75rem] pr-1 mt-2"><Waveform seed={t.id} progress={progress} onSeek={onSeek} active={active} bars={90} height="h-10" testid={`track-waveform-${i}`} /></div>}
    </li>
  );
};

const DistributionInfo = ({ rel }) => {
  const items = [["Distribuzione", rel.distributor], ["UPC", rel.upc], ["Catalogo", rel.catalog_number], ["Etichetta", "The Groove Anomaly records"]].filter(([, v]) => v);
  return (
    <dl className="grid grid-cols-2 gap-3 rounded-xl border border-white/10 bg-white/5 p-4" data-testid="release-distribution-info">
      {items.map(([k, v]) => (
        <div key={k}><dt className="font-mono-tga text-[10px] uppercase tracking-[0.2em] text-zinc-500">{k}</dt><dd className="text-sm font-semibold mt-0.5">{v}</dd></div>
      ))}
    </dl>
  );
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" }) : "");

const MainWave = ({ rel, idx }) => {
  const { progress, onSeek, active, track } = useTrackWave(rel, idx);
  if (!track?.audio_url) return null;
  return (
    <div className="lcd rounded-2xl p-4" data-testid="release-main-waveform-box">
      <p className="font-mono-tga text-[10px] uppercase tracking-[0.2em] text-sky-300/80 mb-2">{String(idx + 1).padStart(2, "0")} · {track.title}</p>
      <Waveform seed={track.id} progress={progress} onSeek={onSeek} active={active} bars={140} height="h-20" variant="lcd" testid="release-main-waveform" />
    </div>
  );
};

export default function ReleaseDetail() {
  const { id } = useParams();
  const [rel, setRel] = useState(undefined);
  const { current } = usePlayer();
  useEffect(() => { api.get(`/releases/${id}`).then((r) => setRel(r.data)).catch(() => setRel(null)); }, [id]);
  useEffect(() => {
    if (!rel) return;
    document.title = rel.seo_title || `${rel.title} - ${rel.artist_name || "The Groove Anomaly"} | The Groove Anomaly records`;
    document.querySelector('link[rel="canonical"]')?.setAttribute("href", `${SITE}/uscite/${rel.id}`);
    const desc = (rel.seo_description || rel.description || `${rel.release_type || "Uscita"} di ${rel.artist_name || ""} su THE GROOVE ANOMALY records: ascolta e acquista la musica.`).slice(0, 160);
    const prevDesc = document.querySelector('meta[name="description"]')?.getAttribute("content");
    setMeta('meta[name="description"]', desc);
    setMeta('meta[property="og:description"]', desc);
    setMeta('meta[property="og:title"]', document.title);
    const el = document.createElement("script");
    el.type = "application/ld+json"; el.id = "tga-release-jsonld"; el.textContent = JSON.stringify(releaseJsonLd(rel));
    document.head.appendChild(el);
    return () => { el.remove(); if (prevDesc) setMeta('meta[name="description"]', prevDesc); };
  }, [rel]);

  if (rel === undefined) return <div className="pt-40 text-center font-mono-tga text-zinc-500">Caricamento…</div>;
  if (rel === null) return <div className="pt-40 text-center" data-testid="release-not-found">Uscita non trovata. <Link to="/uscite" className="text-emerald-400">Torna al catalogo</Link></div>;
  const curIdx = Math.max(0, (rel.tracks || []).findIndex((t) => t.id === current?.id));

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-28" data-testid="release-detail-page">
      <Link to="/uscite" className="inline-flex items-center gap-2 font-mono-tga text-xs uppercase tracking-[0.2em] text-zinc-400 hover:text-white" data-testid="release-back-link"><ArrowLeft className="h-4 w-4" /> Catalogo</Link>
      <div className="grid lg:grid-cols-[1fr_1.1fr] gap-12 mt-8">
        <div className="relative min-w-0">
          <img src={fileUrl(rel.cover_url) || FALLBACK_COVER} alt={rel.title} className="w-full aspect-square object-cover border border-white/15 rounded-2xl shadow-[0_30px_80px_-30px_rgba(31,232,150,0.45)] rise" data-testid="release-cover" />
        </div>
        <div className="space-y-6 min-w-0">
          <p className="font-mono-tga text-xs uppercase tracking-[0.3em] text-emerald-400">{[rel.catalog_number, rel.release_type, rel.genre].filter(Boolean).join(" · ")}</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tighter leading-none break-words [overflow-wrap:anywhere]" data-testid="release-title">{rel.title}</h1>
          {rel.artist_id ? (
            <Link to={`/artisti/${rel.artist_id}`} className="text-xl text-zinc-200 hover:text-emerald-400" data-testid="release-artist-link">{rel.artist_name}</Link>
          ) : <p className="text-xl text-zinc-200">{rel.artist_name}</p>}
          {rel.release_date && <p className="font-mono-tga text-xs text-zinc-500">Uscita: {fmtDate(rel.release_date)}</p>}
          <div className="flex items-center gap-4">
            <PlayButton release={rel} trackIdx={null} testid="release-play-all-btn" className="h-14 w-14" />
            <span className="font-mono-tga text-xs uppercase tracking-[0.2em] text-zinc-400">Ascolta l'uscita</span>
            <span className="ml-auto"><ShareButton sharePath={`release/${rel.id}`} title={`${rel.title} - ${rel.artist_name || "The Groove Anomaly records"}`} testid="release-share-btn" /></span>
          </div>
          {rel.description && <p className="text-base leading-relaxed text-zinc-200 whitespace-pre-line">{rel.description}</p>}
          <MainWave rel={rel} idx={curIdx} />
          <div className="space-y-2">
            <p className="font-mono-tga text-[10px] uppercase tracking-[0.3em] text-zinc-400">Ascolta su</p>
            <PlatformLinks links={rel.links} group="listen" prefix="release-listen" />
          </div>
          <div className="space-y-2">
            <p className="font-mono-tga text-[10px] uppercase tracking-[0.3em] text-hot">Acquista</p>
            <PlatformLinks links={rel.links} group="buy" prefix="release-buy" variant="buy" />
          </div>
          <DistributionInfo rel={rel} />
          <div className="space-y-2">
            <p className="font-mono-tga text-[10px] uppercase tracking-[0.3em] text-zinc-400">Condividi sui social</p>
            <SocialShare releaseId={rel.id} text={`${rel.title} — ${rel.artist_name || "The Groove Anomaly records"}`} prefix="release-social" />
          </div>

          <div className="border-t border-white/10 pt-6">
            <p className="font-mono-tga text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">Tracklist</p>
            <ol className="space-y-1" data-testid="release-tracklist">
              {(rel.tracks || []).map((t, i) => <TrackRow key={t.id} rel={rel} t={t} i={i} />)}
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}
